import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import {
  Banknote, Clock, CheckCircle2, XCircle, Loader2,
  ChevronDown, ChevronUp,
} from "lucide-react";
import { useAuth } from "../../../hooks/useAuth";
import {
  useAdminCashAdvances,
  useApproveCashAdvance,
  useReleaseCashAdvance,
  useRejectCashAdvance,
} from "../../../hooks/useCashAdvance";
import { Modal } from "../../../components/ui/Modal";
import { Button } from "../../../components/ui/Button";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import type { CashAdvance, CashAdvanceStatus } from "../../../lib/cashAdvanceApi";

// ── Constants ─────────────────────────────────────────────────────────────────

function formatCurrency(n: number) {
  return `₱${n.toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const STATUS_TABS: { key: CashAdvanceStatus; label: string }[] = [
  { key: "PENDING",  label: "Pending"  },
  { key: "APPROVED", label: "Approved" },
  { key: "RELEASED", label: "Releasing" },
  { key: "PAID",     label: "Settled"  },
  { key: "REJECTED", label: "Rejected" },
];

const STATUS_CONFIG: Record<CashAdvanceStatus, { badge: string; border: string; icon: typeof Clock }> = {
  PENDING:  { badge: "bg-[#fef9c3] text-[#a16207]",   border: "border-l-[#f59e0b]", icon: Clock        },
  APPROVED: { badge: "bg-[#dbeafe] text-[#1d4ed8]",   border: "border-l-[#3b82f6]", icon: CheckCircle2 },
  RELEASED: { badge: "bg-[#ede9fe] text-[#6d28d9]",   border: "border-l-[#7c3aed]", icon: Banknote     },
  PAID:     { badge: "bg-[#dcfce7] text-[#15803d]",   border: "border-l-[#16a34a]", icon: CheckCircle2 },
  REJECTED: { badge: "bg-[#fee2e2] text-[#b91c1c]",   border: "border-l-[#dc2626]", icon: XCircle      },
};

const INPUT_CLASS = "w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:bg-white focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20";
const LABEL_CLASS = "block text-xs font-medium text-[#374151] mb-1";

// ── Approve Modal ─────────────────────────────────────────────────────────────

function ApproveModal({ ca, businessId, onClose }: { ca: CashAdvance; businessId: string; onClose: () => void }) {
  const mutation = useApproveCashAdvance();

  const handleApprove = () => {
    mutation.mutate(
      { cashAdvId: ca.cashAdvId, payload: { userId: ca.userId, businessId } },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal isOpen onClose={onClose} title="Approve Cash Advance" size="sm">
      <div className="space-y-4">
        <div className="rounded-xl bg-[#f8fafc] border border-[#e2e8f0] px-4 py-3">
          <p className="text-xs text-[#94a3b8] mb-0.5">Amount</p>
          <p className="text-xl font-bold text-[#0f172a]">{formatCurrency(ca.amount)}</p>
          <p className="text-xs text-[#64748b] mt-0.5">{ca.employeeName}</p>
        </div>
        <div className="rounded-lg bg-[#eff3ff] border border-[#c7d2fe] px-4 py-3 text-xs text-[#2447d9]">
          Once approved, the employee will be notified. You can then release the cash when ready.
        </div>
        {mutation.isError && (
          <p className="rounded-lg bg-[#fef2f2] px-3 py-2 text-sm text-[#dc2626]">
            {(mutation.error as Error)?.message?.includes("ALREADY_REVIEWED")
              ? "This request has already been reviewed."
              : "Something went wrong. Please try again."}
          </p>
        )}
        <div className="flex gap-3 pt-1">
          <Button variant="secondary" onClick={onClose} fullWidth>Cancel</Button>
          <button
            onClick={handleApprove}
            disabled={mutation.isPending}
            className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-[#16a34a] px-4 py-2 text-sm font-medium text-white hover:bg-[#15803d] disabled:opacity-50 transition-colors"
          >
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Approve
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Release Modal ─────────────────────────────────────────────────────────────

const releaseSchema = z.object({
  releasedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Required"),
  releaseNote: z.string().max(500).optional(),
});
type ReleaseForm = z.infer<typeof releaseSchema>;

function ReleaseModal({ ca, businessId, onClose }: { ca: CashAdvance; businessId: string; onClose: () => void }) {
  const mutation = useReleaseCashAdvance();
  const { register, handleSubmit, formState: { errors } } = useForm<ReleaseForm>({ resolver: zodResolver(releaseSchema) });

  const onSubmit = (data: ReleaseForm) => {
    mutation.mutate(
      { cashAdvId: ca.cashAdvId, payload: { userId: ca.userId, businessId, releasedAt: data.releasedAt, releaseNote: data.releaseNote } },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal isOpen onClose={onClose} title="Release Cash Advance" size="sm">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="rounded-xl bg-[#f8fafc] border border-[#e2e8f0] px-4 py-3">
          <p className="text-xs text-[#94a3b8] mb-0.5">Releasing to</p>
          <p className="text-sm font-semibold text-[#0f172a]">{ca.employeeName}</p>
          <p className="text-xs text-[#64748b] mt-0.5">{formatCurrency(ca.amount)}</p>
        </div>

        <div>
          <label className={LABEL_CLASS}>Release Date</label>
          <input type="date" className={INPUT_CLASS} {...register("releasedAt")} />
          {errors.releasedAt && <p className="mt-1 text-xs text-[#dc2626]">{errors.releasedAt.message}</p>}
        </div>

        <div>
          <label className={LABEL_CLASS}>Note <span className="text-[#94a3b8] font-normal">(optional)</span></label>
          <textarea rows={3} placeholder="e.g. Handed over in cash at the office…"
            className={INPUT_CLASS + " resize-none"} {...register("releaseNote")} />
        </div>

        <div className="rounded-lg bg-[#ede9fe] border border-[#ddd6fe] px-4 py-3 text-xs text-[#5b21b6]">
          <p className="font-medium mb-0.5">After release:</p>
          <p>{formatCurrency(ca.amount)} will be deducted from {ca.employeeName}'s payslip(s) starting from the next payroll period.</p>
        </div>

        {mutation.isError && (
          <p className="rounded-lg bg-[#fef2f2] px-3 py-2 text-sm text-[#dc2626]">Something went wrong. Please try again.</p>
        )}

        <div className="flex gap-3 pt-1">
          <Button variant="secondary" onClick={onClose} fullWidth>Cancel</Button>
          <button type="submit" disabled={mutation.isPending}
            className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-[#7c3aed] px-4 py-2 text-sm font-medium text-white hover:bg-[#6d28d9] disabled:opacity-50 transition-colors">
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Confirm Release
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Reject Modal ──────────────────────────────────────────────────────────────

const rejectSchema = z.object({
  rejectedReason: z.string().max(500).optional(),
});
type RejectForm = z.infer<typeof rejectSchema>;

function RejectModal({ ca, businessId, onClose }: { ca: CashAdvance; businessId: string; onClose: () => void }) {
  const mutation = useRejectCashAdvance();
  const { register, handleSubmit } = useForm<RejectForm>({ resolver: zodResolver(rejectSchema) });

  const onSubmit = (data: RejectForm) => {
    mutation.mutate(
      { cashAdvId: ca.cashAdvId, payload: { userId: ca.userId, businessId, rejectedReason: data.rejectedReason } },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal isOpen onClose={onClose} title="Reject Cash Advance" size="sm">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="rounded-xl bg-[#f8fafc] border border-[#e2e8f0] px-4 py-3">
          <p className="text-xs text-[#94a3b8] mb-0.5">Request</p>
          <p className="text-sm font-semibold text-[#0f172a]">{ca.employeeName} · {formatCurrency(ca.amount)}</p>
        </div>
        <div>
          <label className={LABEL_CLASS}>Reason <span className="text-[#94a3b8] font-normal">(optional)</span></label>
          <textarea rows={3} placeholder="Explain why the request is being rejected…"
            className={INPUT_CLASS + " resize-none"} {...register("rejectedReason")} />
        </div>
        {mutation.isError && (
          <p className="rounded-lg bg-[#fef2f2] px-3 py-2 text-sm text-[#dc2626]">Something went wrong. Please try again.</p>
        )}
        <div className="flex gap-3 pt-1">
          <Button variant="secondary" onClick={onClose} fullWidth>Cancel</Button>
          <button type="submit" disabled={mutation.isPending}
            className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-[#dc2626] px-4 py-2 text-sm font-medium text-white hover:bg-[#b91c1c] disabled:opacity-50 transition-colors">
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Reject
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Cash Advance Row ──────────────────────────────────────────────────────────

function CashAdvanceRow({ ca, businessId, activeStatus }: {
  ca: CashAdvance; businessId: string; activeStatus: CashAdvanceStatus;
}) {
  const [expanded, setExpanded] = useState(false);
  const [modal, setModal] = useState<"approve" | "release" | "reject" | null>(null);
  const cfg = STATUS_CONFIG[ca.status];

  const { canApproveAction } = useAuth();
  const canApprove = canApproveAction("cash-advance", "approve");
  const canReject = canApproveAction("cash-advance", "reject");
  const canRelease = canApproveAction("cash-advance", "release");

  return (
    <>
      <div className={`rounded-xl border border-[#e2e8f0] border-l-4 ${cfg.border} bg-white overflow-hidden shadow-sm`}>
        <div className="px-5 py-4 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            {/* Status icon */}
            <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${cfg.badge}`}>
              {ca.status === "PENDING"  && <Clock className="h-4 w-4" />}
              {ca.status === "APPROVED" && <CheckCircle2 className="h-4 w-4" />}
              {ca.status === "RELEASED" && <Banknote className="h-4 w-4" />}
              {ca.status === "PAID"     && <CheckCircle2 className="h-4 w-4" />}
              {ca.status === "REJECTED" && <XCircle className="h-4 w-4" />}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <p className="font-semibold text-[#0f172a]">{ca.employeeName ?? "Employee"}</p>
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${cfg.badge}`}>
                  {ca.status.charAt(0) + ca.status.slice(1).toLowerCase()}
                </span>
              </div>
              <p className="text-sm text-[#374151] font-medium">{formatCurrency(ca.amount)}</p>
              <p className="text-xs text-[#94a3b8] mt-0.5">
                Requested {format(new Date(ca.requestedAt), "MMM d, yyyy")}
              </p>
              {ca.status === "RELEASED" && (
                <p className="text-xs text-[#7c3aed] mt-0.5">
                  {formatCurrency(ca.remainingAmount)} remaining · Released {format(new Date(ca.releasedAt!), "MMM d, yyyy")}
                  {ca.releasedBy ? ` by ${ca.releasedBy}` : ""}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
            {activeStatus === "PENDING" && (
              <>
                <button onClick={() => setModal("approve")}
                  disabled={!canApprove}
                  title={!canApprove ? "You don't have permission to approve" : undefined}
                  className="rounded-lg bg-[#16a34a] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#15803d] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  Approve
                </button>
                <button onClick={() => setModal("reject")}
                  disabled={!canReject}
                  title={!canReject ? "You don't have permission to reject" : undefined}
                  className="rounded-lg border border-[#fecaca] bg-[#fef2f2] px-3 py-1.5 text-xs font-medium text-[#dc2626] hover:bg-[#fee2e2] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  Reject
                </button>
              </>
            )}
            {activeStatus === "APPROVED" && (
              <>
                <button onClick={() => setModal("release")}
                  disabled={!canRelease}
                  title={!canRelease ? "You don't have permission to release" : undefined}
                  className="rounded-lg bg-[#7c3aed] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#6d28d9] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  Mark Released
                </button>
                <button onClick={() => setModal("reject")}
                  disabled={!canReject}
                  title={!canReject ? "You don't have permission to reject" : undefined}
                  className="rounded-lg border border-[#fecaca] bg-[#fef2f2] px-3 py-1.5 text-xs font-medium text-[#dc2626] hover:bg-[#fee2e2] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  Reject
                </button>
              </>
            )}
            <button type="button" onClick={() => setExpanded((x) => !x)}
              className="flex h-7 w-7 items-center justify-center rounded-lg border border-[#e2e8f0] text-[#94a3b8] hover:bg-[#f1f5f9] transition-colors">
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* Expanded detail */}
        {expanded && (
          <div className="border-t border-[#f1f5f9] px-5 py-3 bg-[#f8fafc] space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-[#94a3b8]">Employee ID</span>
              <span className="text-[#374151] font-mono text-xs">{ca.userId}</span>
            </div>
            {ca.reason && (
              <div className="flex justify-between gap-4">
                <span className="text-[#94a3b8] shrink-0">Reason</span>
                <span className="text-[#374151] text-right">{ca.reason}</span>
              </div>
            )}
            {ca.approvedBy && (
              <div className="flex justify-between">
                <span className="text-[#94a3b8]">Approved by</span>
                <span className="text-[#374151]">{ca.approvedBy}</span>
              </div>
            )}
            {ca.releasedAt && (
              <div className="flex justify-between">
                <span className="text-[#94a3b8]">Released on</span>
                <span className="text-[#374151]">{format(new Date(ca.releasedAt), "MMM d, yyyy")}</span>
              </div>
            )}
            {ca.releasedBy && (
              <div className="flex justify-between">
                <span className="text-[#94a3b8]">Released by</span>
                <span className="text-[#374151]">{ca.releasedBy}</span>
              </div>
            )}
            {ca.releaseNote && (
              <div className="flex justify-between gap-4">
                <span className="text-[#94a3b8] shrink-0">Release note</span>
                <span className="text-[#374151] text-right">{ca.releaseNote}</span>
              </div>
            )}
            {ca.rejectedReason && (
              <div className="flex justify-between gap-4">
                <span className="text-[#94a3b8] shrink-0">Rejection reason</span>
                <span className="text-[#dc2626] text-right">{ca.rejectedReason}</span>
              </div>
            )}
            {ca.status === "RELEASED" && (
              <div className="pt-2 border-t border-[#e2e8f0]">
                <div className="flex justify-between text-xs text-[#64748b] mb-1.5">
                  <span>Repaid: {formatCurrency(ca.repaidAmount)}</span>
                  <span>Remaining: {formatCurrency(ca.remainingAmount)}</span>
                </div>
                <div className="w-full bg-[#e2e8f0] rounded-full h-1.5">
                  <div
                    className="bg-[#7c3aed] h-1.5 rounded-full transition-all"
                    style={{ width: `${ca.amount > 0 ? Math.min(100, (ca.repaidAmount / ca.amount) * 100) : 0}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {modal === "approve" && <ApproveModal ca={ca} businessId={businessId} onClose={() => setModal(null)} />}
      {modal === "release" && <ReleaseModal ca={ca} businessId={businessId} onClose={() => setModal(null)} />}
      {modal === "reject"  && <RejectModal  ca={ca} businessId={businessId} onClose={() => setModal(null)} />}
    </>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AdminCashAdvancePage() {
  const { activeBusinessId } = useAuth();
  const [activeTab, setActiveTab] = useState<CashAdvanceStatus>("PENDING");
  const [knownCounts, setKnownCounts] = useState<Partial<Record<CashAdvanceStatus, number>>>({});

  const { data, isLoading, error } = useAdminCashAdvances(activeBusinessId, activeTab);
  const items = data?.items ?? [];

  // Track count per tab
  useEffect(() => {
    if (!isLoading && data !== undefined) {
      setKnownCounts((prev) => ({ ...prev, [activeTab]: items.length }));
    }
  }, [isLoading, data, activeTab, items.length]);

  const pendingTotal = items
    .filter((ca) => ca.status === "PENDING")
    .reduce((s, ca) => s + ca.amount, 0);

  return (
    <div className="space-y-5">

      {/* ── Header card ───────────────────────────────────────────────────── */}
      <div className="rounded-xl border border-[#e2e8f0] bg-white px-5 py-4 shadow-sm">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#2447d9]">
              <Banknote className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold text-[#0f172a]">Manage Cash Advances</h1>
              <p className="text-xs text-[#64748b]">Approve, release, and track employee cash advance requests</p>
            </div>
          </div>

          {/* Pending summary — shown when on PENDING tab with items */}
          {activeTab === "PENDING" && !isLoading && items.length > 0 && (
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-[#fef9c3] border border-[#fde68a] px-3 py-1.5 text-center">
                <p className="text-xs text-[#a16207] font-medium">{items.length} request{items.length !== 1 ? "s" : ""}</p>
                <p className="text-sm font-bold text-[#a16207]">{formatCurrency(pendingTotal)}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Tabs ──────────────────────────────────────────────────────────── */}
      <div className="flex gap-1 rounded-lg bg-[#f1f5f9] p-1 w-fit overflow-x-auto overflow-y-hidden">
        {STATUS_TABS.map((tab) => {
          const count = knownCounts[tab.key];
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.key
                  ? "bg-white text-[#0f172a] shadow-sm"
                  : "text-[#64748b] hover:text-[#0f172a]"
              }`}
            >
              {tab.label}
              {count !== undefined && count > 0 && (
                <span className={`text-xs rounded-full px-1.5 py-0.5 font-semibold ${
                  activeTab === tab.key ? "bg-[#eff3ff] text-[#2447d9]" : "bg-[#e2e8f0] text-[#64748b]"
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Content ───────────────────────────────────────────────────────── */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <LoadingSpinner size="lg" className="text-[#2447d9]" />
        </div>
      ) : error ? (
        <div className="rounded-xl border border-[#fecaca] bg-[#fef2f2] px-5 py-4 text-sm text-[#dc2626]">
          Failed to load cash advances. Please try again.
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-[#e2e8f0] bg-white py-16 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#eff3ff]">
            <Banknote className="h-7 w-7 text-[#2447d9]" />
          </div>
          <div>
            <p className="font-semibold text-[#0f172a]">No {STATUS_TABS.find((t) => t.key === activeTab)?.label.toLowerCase()} advances</p>
            <p className="text-xs text-[#94a3b8] mt-0.5">
              {activeTab === "PENDING"
                ? "No requests are waiting for your review."
                : `No cash advances with this status.`}
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {activeBusinessId && items.map((ca) => (
            <CashAdvanceRow
              key={ca.cashAdvId}
              ca={ca}
              businessId={activeBusinessId}
              activeStatus={activeTab}
            />
          ))}
        </div>
      )}
    </div>
  );
}
